import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { FontScaleControl, useCardFontScale } from './QuoteCardScaleContext';

interface Props {
  icon?: React.ReactNode;
  title: string;
  right?: React.ReactNode;
  /** Opt this card into per-card font scaling. When set AND rendered inside a
   *  QuoteCardScaleProvider, the header shows a hover `− % +` control that zooms
   *  the card body; the chosen scale persists per user (keyed by this string).
   *  Omit on the outer section wrappers so they never get a control. */
  scaleKey?: string;
  /** Render the card head in dark slate (matches LegsGrid). */
  dark?: boolean;
  /** Skip the default body padding (use when the body is its own table). */
  flushBody?: boolean;
  /** Make the body flex to fill available height (section becomes a flex column
   *  with a flex-1 body). Pair with a `flex-1` className + a flex-1 child (e.g. a
   *  textarea) so the card grows to fill a short column. */
  fillBody?: boolean;
  /** Show a collapse/expand chevron that hides the body (default true). */
  collapsible?: boolean;
  /** Start collapsed (only meaningful when collapsible). */
  defaultCollapsed?: boolean;
  /** Controlled collapse state. When provided, the card is CONTROLLED: it renders
   *  this value (ignoring its own state / defaultCollapsed) and reports toggles
   *  via onCollapsedChange, so a parent can persist collapse to user settings.
   *  Omit for the default uncontrolled behavior. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Tighter header + body padding — used by the grouped-header trio (Bill To /
   *  Customer / Revenue & Statistics) so they pack together more densely. */
  compact?: boolean;
  /** Headerless / borderless mode: renders just a small inline heading + the
   *  body, with no card border or header strip. Used when the section is folded
   *  into a parent card (the grouped page's "Account Details") to save space. */
  bare?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Standard section card used inside the Quote Detail tabs / drawer. Matches the
 * "qdStyles.card" style from the mockup: light gray header strip with an
 * icon + uppercase title on the left and optional right slot, then a body.
 *
 * The header doubles as a collapse/expand toggle (chevron on the left). The
 * `right` slot stays outside the toggle so its buttons (e.g. "+ Add New") keep
 * working. Collapsed state is local — it resets when the card unmounts.
 */
const Card: React.FC<Props> = ({
  icon, title, right, scaleKey, dark, flushBody, fillBody = false, collapsible = true, defaultCollapsed = false,
  collapsed: collapsedProp, onCollapsedChange,
  compact = false, bare = false, children, className,
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isControlled = collapsedProp !== undefined;
  const collapsed = isControlled ? collapsedProp : internalCollapsed;
  const toggle = () => {
    const next = !collapsed;
    if (!isControlled) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  };
  const open = !collapsible || !collapsed;

  // Per-card font scaling: active only when this card declares a scaleKey AND a
  // provider is present (so standalone pages, which don't wrap the provider, get
  // nothing). Scale zooms the body only; the control lives in the header.
  const { fontScalable, scale, bodyStyle } = useCardFontScale(scaleKey);

  const heading = (
    <h3
      className={[
        'm-0 text-[0.65625rem] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 min-w-0',
        dark ? 'text-white' : 'text-slate-400 dark:text-slate-500',
      ].join(' ')}
    >
      {icon && <span className={dark ? 'text-blue-300 inline-flex' : 'text-blue-600 dark:text-blue-400 inline-flex'}>{icon}</span>}
      <span className="truncate">{title}</span>
    </h3>
  );

  // Bare: no border, header strip, title or icon — just the body. Used to fold
  // a section's fields directly into a parent card (the section's identity is
  // carried by the field labels themselves).
  if (bare) {
    return <div className={`flex flex-col min-w-0 ${className ?? ''}`}>{children}</div>;
  }

  return (
    <section className={`bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-slate-800 dark:border-slate-700 ${fontScalable ? 'group/card-scale' : ''} ${open ? '' : 'self-start'} ${fillBody && open ? 'flex flex-col' : ''} ${className ?? ''}`}>
      {/* Pro-drawer header style: no gray strip — icon + muted uppercase title on
          the left, the chevron on the right, separated from the body by a dashed
          underline when open. Matches src/pages/pro/components/orderform/Section. */}
      <div className={[compact ? 'px-2.5 pt-1.5' : 'px-3 pt-2', dark ? 'bg-slate-800' : ''].join(' ')}>
        <div
          className={[
            'flex items-center justify-between gap-2',
            open
              ? `${compact ? 'pb-1.5 mb-1' : 'pb-2 mb-1'} border-b border-dashed ${dark ? 'border-slate-700' : 'border-slate-200 dark:border-slate-700'}`
              : (compact ? 'pb-1.5' : 'pb-2'),
          ].join(' ')}
        >
          {collapsible ? (
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
              className="flex items-center gap-1.5 min-w-0 flex-1 cursor-pointer bg-transparent border-0 p-0 text-left"
            >
              {heading}
            </button>
          ) : (
            heading
          )}
          <div className="flex items-center gap-2 flex-shrink-0">
            {right && <div className={dark ? 'text-blue-300' : ''}>{right}</div>}
            <FontScaleControl scaleKey={scaleKey} title={title} />
            {collapsible && (
              <span
                aria-hidden="true"
                onClick={toggle}
                className="inline-flex cursor-pointer"
              >
                <ChevronDownIcon
                  strokeWidth={2.5}
                  className={[
                    'h-3 w-3 flex-shrink-0 transition-transform',
                    open ? '' : '-rotate-90',
                    dark ? 'text-blue-300' : 'text-slate-400 dark:text-slate-500',
                  ].join(' ')}
                />
              </span>
            )}
          </div>
        </div>
      </div>
      {open && (
        <div
          className={[flushBody ? '' : (compact ? 'px-2.5 pb-2' : 'px-3 pb-2.5'), fillBody ? 'flex-1 flex flex-col min-h-0' : ''].join(' ')}
          style={bodyStyle}
          data-font-scale={fontScalable ? scale : undefined}
        >
          {children}
        </div>
      )}
    </section>
  );
};

export default Card;
